// Page: AdminEnterprises
// Purpose: Entry page for enterprise admin actions. Validates admin access and
// links to create/manage enterprise flows.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { Building2, Plus, Settings, ArrowLeft } from 'lucide-react';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function AdminEnterprises() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if user is the admin using the same pattern as other admin pages
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // Enterprise admin pages
  const enterprisePages = [
    {
      title: 'Create Enterprise',
      description: 'Create new enterprise accounts with custom plan configurations',
      icon: Plus,
      path: '/app/private/createEnterprise',
      color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    },
    {
      title: 'Manage Enterprises',
      description: 'View and manage existing enterprise accounts',
      icon: Settings,
      path: '/app/private/manageEnterprises',
      color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    }
  ];

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading enterprises admin..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/app/private/admin')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mr-4 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-lg p-1"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="text-sm font-medium">Back to Admin</span>
          </button>
        </div>
        <div className="flex items-center mb-4">
          <Building2 className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Enterprise Management</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Create and manage enterprise accounts and configurations.
        </p>
      </div>

      {/* Enterprise pages grid */}
      <div className="flex flex-col gap-4">
        {enterprisePages.map((page, index) => (
          <button
            key={page.path || `enterprise-page-${index}`}
            onClick={page.path ? () => navigate(page.path) : () => {}}
            className="flex items-center w-full p-6 bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            <div className={`p-2 rounded-lg ${page.color} mr-4`}>
              <page.icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {page.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {page.description}
              </p>
            </div>
            <div className="w-6 flex items-center justify-center h-5">
              <svg className="h-4 w-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      <Footer />
    </div>
  );
} 